import React, { useState, useLayoutEffect } from "react";
import { DynamicTabs } from "../components/DynamicTabs/DynamicTabs";
import { TopMenuBar } from "../components/TopMenuBar/TopMenuBar";
import ProgressBar from "../components/ProgressBar/ProgressBar";
import PackoutNameForm from "../components/PackoutNameForm/PackoutNameForm";
import NavBar from "../components/NavBar/NavBar";
import FilterIcon from "../components/FilterIcon/FilterIcon";
import FilterSideDrawer from "../components/FilterSideDrawer/FilterSideDrawer";

import * as Utils from "../Utils";
import * as XLSX from "xlsx";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import "./style.css";
import "@fontsource-variable/open-sans";

export const ContainerOverviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [rowsCounter, setRowsCounter] = useState([]);
  const [rows, setRows] = useState(null); // ✅ make rows reactive
  const [openPanel, setOpenPanel] = useState(false);
  const [isFilters, setIsFilters] = useState(false);
  const [NIINfilters, setNIINfilters] = useState([]);

  const [numberOfTabs, setNumberOfTabs] = useState(0);
  const [isu90tabCount, setIsu90tabCount] = useState(0);
  const [flat463LtabCount, setFlat463LtabCount] = useState(0);
  const [c463LtabCount, setC463LtabCount] = useState(0);
  const [nfevtabCount, setNfevtabCount] = useState(0);
  const [pallet80x120tabCount, setPallet80x120tabCount] = useState(0);
  const [pallet100x120tabCount, setPallet100x120tabCount] = useState(0);

  // Load data from stores
  async function waitForSetRows() {
    const tabCol = await Utils.getAllContainersUpdateFromStore();
    setNumberOfTabs(tabCol.length);

    // Count container types
    setIsu90tabCount(tabCol.filter((e) => e.containertype === "ISU-90").length);
    setFlat463LtabCount(
      tabCol.filter((e) => e.containertype === "463L").length,
    );
    setC463LtabCount(tabCol.filter((e) => e.containertype === "C463L").length);
    setNfevtabCount(tabCol.filter((e) => e.containertype === "NFEV").length);
    setPallet80x120tabCount(
      tabCol.filter((e) => e.containertype === "80x120").length,
    );
    setPallet100x120tabCount(
      tabCol.filter((e) => e.containertype === "100x120").length,
    );

    // Load items
    const currentRunId = Utils.getCurrentRunId();
    const items = await Utils.getAllItemsPlacedBoxesStore(currentRunId);

    console.log("items+ "+ JSON.stringify(items));
    setRows(items ?? []);
    setRowsCounter(items ?? []);
    setLoading(false);
  }

  useLayoutEffect(() => {
    waitForSetRows();
  }, []);

  // Handle Apply / Clear filters
  const handleApply = (filters) => {
    console.log("Drawer apply NIIN list:", filters.niin);
    setNIINfilters(filters.niin);
    setIsFilters(filters.niin.length > 0);
    setOpenPanel(false);
  };

  // Filter rows reactively
  const filteredRows = (rows ?? [])
    .flat()
    .filter(
      (row) =>
        NIINfilters.length === 0 ||
        NIINfilters.some((filter) =>
          row.niin?.toLowerCase().includes(filter.toLowerCase()),
        ),
    );

  // Row parser for DynamicTabs
  function rowparser(containerID, containertype) {
    return filteredRows.filter(
      (element) =>
        element.palletid === containerID &&
        element.storagetype === containertype,
    );
  }

  function renderTabs() {
    if (!rows) return <div>Loading...</div>;

    const filterKey =
      NIINfilters.length > 0 ? `tabs-${NIINfilters.join("-")}` : "tabs-empty";

    return (
      <DynamicTabs
        key={filterKey} // 🔥 FORCE React to reset tabs and apply the filter strictly
        rowsCounter={rowsCounter}
        rows={filteredRows}
        numberOfTabs={numberOfTabs}
        isu90tabCount={isu90tabCount}
        flat463LtabCount={flat463LtabCount}
        c463LtabCount={c463LtabCount}
        nfevtabCount={nfevtabCount}
        pallet80x120tabCount={pallet80x120tabCount}
        pallet100x120tabCount={pallet100x120tabCount}
        whichTable={0}
        totalWeights={0}
      />
    );
  }

  const downloadRows = () => {
    // 1. Create a new Workbook
    const wb = XLSX.utils.book_new();

    // 2. Define the container types and their respective counts from your state
    const containerMapping = [
      { type: "ISU-90", count: isu90tabCount },
      { type: "463L", count: flat463LtabCount },
      { type: "C463L", count: c463LtabCount },
      { type: "NFEV", count: nfevtabCount },
      { type: "80x120", count: pallet80x120tabCount },
      { type: "100x120", count: pallet100x120tabCount },
    ];

    // Define the strict list of allowed columns
    const targetColumns = [
      "id",
      "box_id",
      "nsn",
      "niin",
      "nomenclature",
      "qty",
      "authorizedqty",
      "weight",
      "length",
      "width",
      "height",
      "level",
      "quadid",
      "palletid",
      "storagetype",
      "spillno",
      "created_at",
      "updated_at",
      "ksn",
      "shopcode",
      "location",
      "postionOrder",
    ];

    // 3. Iterate through each container type
    containerMapping.forEach(({ type, count }) => {
      for (let i = 1; i <= count; i++) {
        const sheetData = rowparser(i, type);

        if (sheetData && sheetData.length > 0) {
          // --- THE FIX: Filter out unlisted properties ---
          const filteredData = sheetData.map((row) => {
            const filteredRow = {};
            targetColumns.forEach((key) => {
              // If the key doesn't exist in the row, default it to null or empty string
              filteredRow[key] = row[key] !== undefined ? row[key] : "";
            });
            return filteredRow;
          });

          // Convert the strictly filtered data into a worksheet
          const ws = XLSX.utils.json_to_sheet(filteredData, {
            header: targetColumns,
          });

          const sheetName = `${type}_${i}`.substring(0, 31);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        }
      }
    });

    // 4. Generate the Excel file and trigger the download
    if (wb.SheetNames.length > 0) {
      XLSX.writeFile(
        wb,
        `Container_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } else {
      console.error("No data found to download");
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="containerOverview">
      <FilterSideDrawer
        isOpen={openPanel}
        onClose={() => setOpenPanel(false)}
        onApply={handleApply}
        defaultNiin={NIINfilters}
      />
      <TopMenuBar />
      <div className="under-banner">
        <ProgressBar page="placement" />
        <div className="div">
          <div className="frame-2">
            <div className="name-form">
              <div className="left-side">
                <PackoutNameForm />
                <p className="directions">
                  <span className="p-bold">3. Review pallet requirements.</span>{" "}
                  Below is an overview of the pallet requirements for your
                  selected job:
                </p>
              </div>
              <div className="header-row">
                <IconButton
                  onClick={() => {
                    downloadRows();
                  }}
                  aria-label="download"
                  className="download-btn"
                  size="large"
                >
                  <DownloadIcon />
                </IconButton>

                <FilterIcon
                  onClick={() => setOpenPanel(true)}
                  inUse={isFilters}
                />
              </div>
            </div>
            <div className="frame-3">{renderTabs()}</div>
          </div>
          <NavBar>
            <button className="button-wrapper">
              <Button href="#/packingListPage" className="button-7">
                Back
              </Button>
            </button>
            <button className="button-8">
              <Button href="#/finalPackingPage" className="button-9">
                Next Step
              </Button>
            </button>
          </NavBar>
        </div>
      </div>
    </div>
  );
};

export default ContainerOverviewPage;
